import { FC, useRef } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Paper, Stack } from '@mui/material';
import dayjs from 'dayjs';
import CatchValue from '../../../Models/ReportModels/CatchValue';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import React from 'react';
import { FilterPayload } from './Filters/FilterDefinition';

interface CatchValueReportProps {
  apiRef: React.MutableRefObject<GridApiCommunity>;
  dateFrom: string;
  setDateFrom: (date: string) => void;
  dateTo: string;
  setDateTo: (date: string) => void;
  loading: boolean;
  catchValues: CatchValue[];
  fetchCatchValue: (
    filterModel?: FilterPayload,
    dateFrom?: string,
    dateTo?: string
  ) => Promise<void>;
}

const CatchValueReport: FC<CatchValueReportProps> = (props) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [error, setError] = React.useState<string>('');
  const MAX_DATE_RANGE_YEARS = 2;
  const rangeTooLarge = (from: string, to: string) =>
    Math.abs(dayjs(to, 'DD.MM.YYYY').diff(dayjs(from, 'DD.MM.YYYY'), 'year', true)) >= MAX_DATE_RANGE_YEARS;


  const columns: GridColDef[] = [
    {
      field: 'shipRegistrationNumber',
      headerName: 'Skipaskrárnúmer',
      flex: 1,
      minWidth: 100,
      sortable: true,
    },
    {
      field: 'shipName',
      headerName: 'Skip heiti',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'landingDate',
      headerName: 'Löndunardagur',
      flex: 1,
      minWidth: 120,
      sortable: true,
      filterable: false,
      valueFormatter: (params) => dayjs(params.value).format('DD.MM.YYYY'),
    },
    {
      field: 'buyerName',
      headerName: 'Kaupandi',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'sellerName',
      headerName: 'Seljandi',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'quantity',
      headerName: 'Magn',
      flex: 1,
      minWidth: 100,
      sortable: true,
      filterable: false,
      type: 'number',
    },
    {
      field: 'value',
      headerName: 'Verðmæti',
      flex: 1,
      minWidth: 120,
      sortable: true,
      filterable: false,
      type: 'number',
      valueFormatter: (params) => `${params.value.toLocaleString('is-IS')} kr.`,
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="is">
      <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <DatePicker
              label="Frá"
              value={dayjs(props.dateFrom, 'DD.MM.YYYY')}
              onError={(_, value) => setError(value ? 'Dagsetning ekki í réttu formi.' : '')}
              slotProps={{ textField: { helperText: error || undefined, error: !!error, required: true, size: 'small', sx: { minWidth: 150 } } }}
              onChange={(date) => {
                if (!date) return;
                const formatted = date.format('DD.MM.YYYY');
                props.setDateFrom(formatted);
                setError(rangeTooLarge(formatted, props.dateTo) ? 'Tímabil má ekki vera stærra en 2 ár' : '');
              }}
              disableFuture
            />
            <DatePicker
              label="Til"
              value={dayjs(props.dateTo, 'DD.MM.YYYY')}
              onChange={(date) => {
                if (!date) return;
                const formatted = date.format('DD.MM.YYYY');
                props.setDateTo(formatted);
                setError(rangeTooLarge(props.dateFrom, formatted) ? 'Tímabil má ekki vera stærra en 2 ár' : '');
              }}
              disableFuture
              slotProps={{ textField: { helperText: error || undefined, error: !!error, required: true, size: 'small', sx: { minWidth: 150 } } }}
            />
          </Stack>
        </Box >
        <Box sx={{ flexGrow: 1, height: '100%', minHeight: '400px' }}>
          <div ref={gridRef} style={{ height: '100%' }}>
            <DataGrid
              apiRef={props.apiRef}
              disableColumnFilter
              rows={Array.isArray(props.catchValues) ? props.catchValues.map((row, idx) => ({ ...row, _rowId: idx })) : []}
              columns={columns}
              loading={props.loading}
              getRowId={(row) => row._rowId}
              density="standard"
              autoHeight={false}
              rowHeight={48}
              getRowHeight={() => 48}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'landingDate', sort: 'desc' }],
                },
              }}
              sx={{
                minHeight: '400px',
                maxHeight: '600px',
                '& .MuiDataGrid-paper': {
                  minWidth: 380,
                  maxWidth: 420,
                },
                '& .MuiDataGrid-filterForm': {
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  width: '100%',
                  gap: 2,
                },
              }}
              localeText={{
                noRowsLabel: "Engar niðurstöður",
                toolbarFilters: "Síur",
                toolbarFiltersTooltipHide: "Fela síur",
                toolbarFiltersTooltipShow: "Sýna síur",
                columnHeaderFiltersLabel: "Sía eftir",
                filterOperatorContains: "Inniheldur",
                filterOperatorEquals: "Jafnt og",
                filterOperatorStartsWith: "Byrjar á",
                filterOperatorEndsWith: "Endar á",
                filterOperatorIs: "Er",
                filterOperatorNot: "Er ekki",
                filterOperatorIsEmpty: "Er tómt",
                filterOperatorIsNotEmpty: "Er ekki tómt",
                filterValueAny: "(einhver)",
                filterValueTrue: "satt",
                filterValueFalse: "ósatt",
                filterOperatorIsAnyOf: "Er einhver af",
                toolbarColumns: "Dálkar",
                toolbarColumnsLabel: "Dálkar",
                columnsPanelTextFieldLabel: "Finna dálk",
                columnsPanelTextFieldPlaceholder: "Leita að dálki",
                columnsPanelDragIconLabel: "Endurraða dálkum",
                columnsPanelShowAllButton: "Sýna alla",
                columnsPanelHideAllButton: "Fela alla",
                filterPanelColumns: "Dálkar",
                filterPanelOperator: "Skilyrði",
                filterPanelInputLabel: "Gildi",
                filterPanelInputPlaceholder: "Sía gildi",
                filterPanelAddFilter: "Bæta við síu",
                filterPanelDeleteIconLabel: "Eyða",
                columnMenuLabel: "Valmynd",
                columnMenuShowColumns: "Sýna dálka",
                columnMenuFilter: "Sía",
                columnMenuHideColumn: "Fela",
                columnMenuUnsort: "Hætta að raða",
                columnMenuSortAsc: "Raða hækkandi",
                columnMenuSortDesc: "Raða lækkandi",
                columnMenuManageColumns: "Stjórna dálkum",
              }}
            />
          </div>
        </Box>
      </Paper >
    </LocalizationProvider>
  );
};

export default CatchValueReport;